// One person's expected pay for one month, from their income plan and what happened that month.
//
// Income plans (person.plans, each valid from a month until the next one starts):
//   { mode: 'employee', base, commission, mealPerDay, mealType: 'card'|'cash'|'none',
//     allowance, allowanceType: 'fixed'|'reimbursement', allowanceTaxable,
//     subsidies: { mode: 'lump'|'twelfths'|'half', holidayMonth, christmasMonth }, startDate, table, dependents,
//     ssRate, weeklyDays, vacationDays, vacationMonth,
//     deductions: [{ id, label, pct, of: 'commission'|'base'|'gross', fixed, enabled }] }
//   { mode: 'net', net, mealCard, fourteen, holidayMonth, christmasMonth }   — just the take-home amount
//   { mode: 'none' }                                                         — no income (between jobs, etc.)
//
// What happened in the month (all optional): { sickDays, absenceDays, vacationDays, mealDays, commissionPct,
//   extraPay: [{ label, amount, taxable, ss }] }

import { r2, sum } from './money.js';
import { businessDays, daysInMonth, daysInYear, daysBetween, monthNum, yearOf, isDate, lastDay, firstDay } from './calendar.js';
import { withholding } from './tax/withholding.js';
import { irsJovemCap } from './tax/params.js';

export const DEFAULT_EMPLOYEE_PLAN = {
  mode: 'employee',
  base: 0,
  commission: 0,
  mealPerDay: 0,
  mealType: 'card',
  allowance: 0,
  // 'fixed': paid every month as part of the pay. 'reimbursement': money back for expenses paid out of pocket, only
  // when claimed; never part of the pay (the amount is just the monthly limit, for reference).
  allowanceType: 'fixed',
  allowanceTaxable: false,
  allowanceLabel: 'Allowance',
  subsidies: { mode: 'lump', holidayMonth: 6, christmasMonth: 12 },
  commissionSS: true,
  yearlyBonus: 0,
  yearlyBonusMonth: 12,
  startDate: '',
  table: 'I',
  dependents: 0,
  ssRate: 11,
  weeklyDays: 5,
  vacationDays: 22,
  vacationMonth: 8,
  deductions: [],
};

export const withPlanDefaults = (plan) => {
  if (!plan || plan.mode === 'none') return { mode: 'none', ...(plan || {}) };
  if (plan.mode === 'net') return { net: 0, mealCard: 0, fourteen: false, holidayMonth: 6, christmasMonth: 12, ...plan };
  return { ...DEFAULT_EMPLOYEE_PLAN, ...plan, subsidies: { ...DEFAULT_EMPLOYEE_PLAN.subsidies, ...(plan.subsidies || {}) } };
};

const num = (v, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const clampDays = (v) => Math.max(0, num(v));

// Share of a month the person was employed (30-day convention used in Portuguese payroll)
export function employedFraction(monthKey, startDate, endDate) {
  const first = firstDay(monthKey);
  const last = lastDay(monthKey);
  const from = isDate(startDate) && startDate > first ? startDate : first;
  const to = isDate(endDate) && endDate < last ? endDate : last;
  if (to < from) return 0;
  const days = daysBetween(from, to) + 1;
  if (from === first && to === last) return 1;
  return Math.min(1, days / 30);
}

/* ── Employee ───────────────────────────────────────────── */

function employeeSlip(plan, monthKey, inputs, ctx) {
  const { params, jovem, holidayOpts, capUsed = 0, paymentsBefore = 0, appliedAtSource = true } = ctx;
  const notes = [];
  const y = yearOf(monthKey);
  const m = monthNum(monthKey);
  const start = isDate(plan.startDate) ? plan.startDate : null;
  const end = isDate(plan.endDate) ? plan.endDate : null;
  if (start && start > lastDay(monthKey)) return emptySlip('employee', ['Not started yet']);
  if (end && end < firstDay(monthKey)) return emptySlip('employee', ['Contract ended']);

  const fraction = employedFraction(monthKey, start, end);
  if (fraction < 1) notes.push(`Part month: paid for ${Math.round(fraction * 30)} of 30 days`);

  // Days
  const workDays = businessDays(monthKey, holidayOpts, { from: start, until: end });
  const sick = clampDays(inputs.sickDays);
  const absence = clampDays(inputs.absenceDays);
  // Holidays: what the review says, otherwise the usual holiday month gets the year's days (not in the first year)
  const typedVacation = inputs.vacationDays !== undefined && inputs.vacationDays !== null && inputs.vacationDays !== '';
  const firstYearOfJob = start && yearOf(start.slice(0, 7)) === y;
  const vacation = typedVacation ? clampDays(inputs.vacationDays)
    : m === Number(plan.vacationMonth || 0) && !firstYearOfJob ? Math.min(clampDays(plan.vacationDays), workDays.length) : 0;
  const unpaidDays = sick + absence;
  const mealDays = inputs.mealDays !== undefined && inputs.mealDays !== null && inputs.mealDays !== ''
    ? clampDays(inputs.mealDays)
    : Math.max(0, workDays.length - sick - absence - vacation);

  // Pay
  const base = num(plan.base);
  const dailyRate = (base * 12) / (52 * Math.max(1, num(plan.weeklyDays, 5)));
  const basePay = r2(Math.max(0, base * fraction - unpaidDays * dailyRate));
  const commissionPct = inputs.commissionPct === undefined || inputs.commissionPct === null ? 100 : num(inputs.commissionPct, 100);
  const commission = r2(num(plan.commission) * fraction * (commissionPct / 100));

  // Meal allowance: tax-free up to the daily limit for the way it's paid; any excess is taxed like salary
  const perDay = num(plan.mealPerDay);
  const limit = plan.mealType === 'cash' ? params.mealExempt.cash : plan.mealType === 'card' ? params.mealExempt.card : 0;
  const meal = plan.mealType === 'none' ? 0 : r2(perDay * mealDays);
  const mealTaxable = plan.mealType === 'none' ? 0 : r2(Math.max(0, perDay - limit) * mealDays);
  if (mealTaxable > 0) notes.push(`Meal allowance above the tax-free ${limit.toFixed(2)} €/day: ${mealTaxable.toFixed(2)} € is taxed`);

  const reimbursementOnly = plan.allowanceType === 'reimbursement';
  const allowance = reimbursementOnly ? 0 : r2(num(plan.allowance) * fraction);
  const allowanceTaxable = plan.allowanceTaxable ? allowance : 0;

  // Yearly bonus (e.g. "Obj. Qual."): paid with the salary in its month, taxed like it
  const bonusMonth = Number(plan.yearlyBonusMonth) || 12;
  const yearlyBonus = num(plan.yearlyBonus) > 0 && m === bonusMonth && fraction > 0 ? [{ label: plan.yearlyBonusLabel || 'Yearly bonus', amount: num(plan.yearlyBonus) }] : [];
  const extras = [...yearlyBonus, ...(inputs.extraPay || [])].filter((x) => num(x.amount) !== 0);
  const extraTaxable = sum(extras.filter((x) => x.taxable !== false).map((x) => num(x.amount)));
  const extraSS = sum(extras.filter((x) => x.ss !== false && x.taxable !== false).map((x) => num(x.amount)));
  const extraTotal = sum(extras.map((x) => num(x.amount)));

  // Holiday and Christmas bonuses (one month of base salary each)
  const sub = plan.subsidies || {};
  const subsidyPayments = [];
  const hm = Number(sub.holidayMonth) || 6;
  const cm = Number(sub.christmasMonth) || 12;
  const hiringYear = start && yearOf(start.slice(0, 7)) === y;
  const endYear = end && yearOf(end.slice(0, 7)) === y;
  // Christmas bonus is proportional to the time worked in the year it starts or ends
  const yearFraction = (() => {
    const from = hiringYear ? start : `${y}-01-01`;
    const to = endYear ? end : `${y}-12-31`;
    return Math.max(0, Math.min(1, (daysBetween(from, to) + 1) / daysInYear(y)));
  })();
  const addSubsidy = (label, full, share) => {
    const amount = r2(full * share);
    if (amount > 0) subsidyPayments.push({ label, amount, rateBase: full });
  };
  const mode = sub.mode || 'lump';
  if (mode === 'lump' || mode === 'half') {
    const share = mode === 'half' ? 0.5 : 1;
    // In the first year the holiday bonus comes with the holidays earned that year, normally taken the next year
    if (m === hm && !hiringYear) addSubsidy('Holiday bonus', base, share);
    if (m === cm || (endYear && monthKey === end.slice(0, 7) && m < cm)) addSubsidy('Christmas bonus', base * yearFraction, share);
  }
  if (mode === 'twelfths' || mode === 'half') {
    const share = mode === 'half' ? 0.5 / 12 : 1 / 12;
    if (fraction > 0) {
      if (!hiringYear) addSubsidy('Holiday bonus (twelfth)', base, share);
      addSubsidy('Christmas bonus (twelfth)', base, share);
    }
  }
  if (hiringYear && m === hm && mode !== 'twelfths') notes.push('No holiday bonus in the first year: it comes with the holidays earned, usually the next year');

  const subsidies = sum(subsidyPayments.map((s) => s.amount));

  // Social security: everything that counts as pay, bonuses included
  const ssRate = num(plan.ssRate, params.socialSecurity.employee) / 100;
  // Commission normally pays social security too; some employers pay it in a way that doesn't (see the offer letter)
  const commissionSS = plan.commissionSS === false ? 0 : commission;
  const ssBase = r2(basePay + commissionSS + subsidies + mealTaxable + allowanceTaxable + extraSS);
  const ss = r2(ssBase * ssRate);

  // IRS withholding: the month's pay on its own, each bonus on its own
  const R = r2(basePay + commission + mealTaxable + allowanceTaxable + extraTaxable);
  const pctJ = jovem && jovem.eligible && appliedAtSource ? jovem.pct : 0;
  const monthlyCap = irsJovemCap(params) / 14;
  let used = capUsed;
  let payments = paymentsBefore;
  const withheld = [];
  const pay = (label, amount, rateBase) => {
    if (!(amount > 0)) return;
    payments += label === 'Salary' ? 1 : amount / Math.max(1, rateBase || amount);
    const room = Math.max(0, monthlyCap * payments - used);
    let w;
    if (rateBase && rateBase !== amount) {
      // A twelfth of a bonus: the rate is the one for the whole bonus
      const whole = withholding(rateBase, params, { table: plan.table, dependents: plan.dependents, jovemPct: pctJ, exemptRoom: Infinity });
      const exempt = pctJ ? r2(Math.min(amount * (pctJ / 100), room)) : 0;
      const rate = pctJ ? whole.effRate : whole.full / rateBase * 100;
      w = { amount: Math.floor(((amount - exempt) * rate) / 100 + 1e-9), effRate: rate, exempt, taxable: r2(amount - exempt) };
    } else {
      w = withholding(amount, params, { table: plan.table, dependents: plan.dependents, jovemPct: pctJ, exemptRoom: room });
    }
    // One-off adjustment for this month (e.g. November 2026: only half is withheld)
    const adj = (params.withholdingAdjustments || []).find((a) => a.month === monthKey);
    if (adj && w.amount > 0) {
      const rows = (params.withholding[plan.table] || params.withholding.I).rows;
      const penultimate = rows.length > 1 ? rows[rows.length - 2].upTo : Infinity;
      if (!(adj.exceptAbovePenultimate && amount > penultimate)) w = { ...w, amount: Math.floor(w.amount * adj.factor + 1e-9), adjusted: adj.factor };
    }
    used = r2(used + (w.exempt || 0));
    withheld.push({ label, base: amount, amount: w.amount, effRate: w.effRate, exempt: w.exempt || 0, adjusted: w.adjusted || null });
  };
  pay('Salary', R);
  for (const s of subsidyPayments) pay(s.label, s.amount, s.rateBase === s.amount ? undefined : s.rateBase);
  const irs = sum(withheld.map((w) => w.amount));
  if (jovem && jovem.eligible && !appliedAtSource) notes.push('IRS Jovem not applied by the employer: the difference comes back in the yearly IRS refund');
  if (withheld.some((w) => w.adjusted)) notes.push(`Only ${Math.round(withheld.find((w) => w.adjusted).adjusted * 100)}% of the usual IRS is withheld this month (the 2026 IRS cut)`);

  // Other deductions after tax (e.g. a share of commission, union fees)
  const deductions = (plan.deductions || []).filter((d) => d.enabled !== false).map((d) => {
    const of = d.of === 'commission' ? commission : d.of === 'base' ? basePay : basePay + commission + subsidies;
    const amount = r2(num(d.fixed) + of * (num(d.pct) / 100));
    return { label: d.label || 'Deduction', amount };
  }).filter((d) => d.amount !== 0);
  const otherDeductions = sum(deductions.map((d) => d.amount));

  const mealCash = plan.mealType === 'cash' ? meal : 0;
  const mealCard = plan.mealType === 'card' ? meal : 0;
  const grossCash = r2(basePay + commission + subsidies + allowance + extraTotal + mealCash);
  const netBank = r2(grossCash - ss - irs - otherDeductions);

  // For the yearly IRS: employment income subject to IRS (tax-free meal allowance and untaxed allowance excluded)
  const taxable = r2(basePay + commission + subsidies + mealTaxable + allowanceTaxable + extraTaxable);

  const lines = [
    { key: 'base', label: fraction < 1 || unpaidDays ? `Base salary (${basePay === 0 ? 'none' : 'adjusted'})` : 'Base salary', amount: basePay },
    commission ? { key: 'commission', label: commissionPct !== 100 ? `Commission (${commissionPct}% of target)` : 'Commission', amount: commission } : null,
    ...subsidyPayments.map((s) => ({ key: 'subsidy', label: s.label, amount: s.amount })),
    ...extras.map((x) => ({ key: 'extra', label: x.label || 'Extra pay', amount: r2(num(x.amount)) })),
    allowance ? { key: 'allowance', label: plan.allowanceLabel || 'Expenses allowance', amount: allowance } : null,
    mealCash ? { key: 'meal', label: `Meal allowance (${mealDays} days, cash)`, amount: mealCash } : null,
    { key: 'ss', label: `Social security (${num(plan.ssRate, 11)}%)`, amount: -ss },
    { key: 'irs', label: pctJ ? `IRS withheld (IRS Jovem ${pctJ}% exempt)` : 'IRS withheld', amount: -irs },
    ...deductions.map((d) => ({ key: 'deduction', label: d.label, amount: -d.amount })),
  ].filter(Boolean);

  return {
    mode: 'employee',
    lines,
    netBank,
    mealCard,
    allowance,
    reimbursementLimit: reimbursementOnly ? num(plan.allowance) : 0,
    gross: r2(grossCash + mealCard),
    taxable,
    ss,
    irs,
    otherDeductions,
    subsidies,
    withheld,
    days: { work: workDays.length, meal: mealDays, sick, absence, vacation, vacationAssumed: !typedVacation && vacation > 0, unpaid: unpaidDays },
    jovem: pctJ ? { pct: pctJ, index: jovem.index } : null,
    capUsed: used,
    payments,
    notes,
  };
}

function emptySlip(mode, notes = []) {
  return {
    mode, lines: [], netBank: 0, mealCard: 0, allowance: 0, reimbursementLimit: 0, gross: 0, taxable: 0, ss: 0, irs: 0, otherDeductions: 0,
    subsidies: 0, withheld: [], days: null, jovem: null, capUsed: undefined, payments: undefined, notes,
  };
}

/* ── Take-home only ─────────────────────────────────────── */

function netSlip(plan, monthKey, inputs) {
  const m = monthNum(monthKey);
  const net = num(plan.net);
  const extra = plan.fourteen && (m === Number(plan.holidayMonth || 6) || m === Number(plan.christmasMonth || 12)) ? net : 0;
  // Yearly bonus (e.g. "Obj. Qual."): paid with the salary in its month, taxed like it
  const bonusMonth = Number(plan.yearlyBonusMonth) || 12;
  const yearlyBonus = num(plan.yearlyBonus) > 0 && m === bonusMonth && fraction > 0 ? [{ label: plan.yearlyBonusLabel || 'Yearly bonus', amount: num(plan.yearlyBonus) }] : [];
  const extras = [...yearlyBonus, ...(inputs.extraPay || [])].filter((x) => num(x.amount) !== 0);
  const extraTotal = sum(extras.map((x) => num(x.amount)));
  const netBank = r2(net + extra + extraTotal);
  const lines = [
    { key: 'net', label: 'Take-home pay', amount: net },
    extra ? { key: 'subsidy', label: m === Number(plan.holidayMonth || 6) ? 'Holiday bonus' : 'Christmas bonus', amount: extra } : null,
    ...extras.map((x) => ({ key: 'extra', label: x.label || 'Extra pay', amount: r2(num(x.amount)) })),
  ].filter(Boolean);
  return { ...emptySlip('net'), lines, netBank, mealCard: r2(num(plan.mealCard)), gross: r2(netBank + num(plan.mealCard)) };
}

// ctx: { params, jovem, holidayOpts, capUsed, paymentsBefore, appliedAtSource }
export function payslip(planIn, monthKey, inputs = {}, ctx = {}) {
  const plan = withPlanDefaults(planIn);
  if (plan.mode === 'none') return emptySlip('none', ['No income']);
  if (plan.mode === 'net') return netSlip(plan, monthKey, inputs || {});
  return employeeSlip(plan, monthKey, inputs || {}, ctx);
}

export const workDaysIn = (monthKey, holidayOpts) => businessDays(monthKey, holidayOpts).length;
export const daysOf = daysInMonth;
